import { BoxGeometry, ConeGeometry, CylinderGeometry, Group, Mesh, MeshBasicMaterial, MeshLambertMaterial, type BufferGeometry, type Material } from 'three';
import { checkBoxes, type AttackField } from '../../core/boss/attacks';
import { cellX, cellY } from '../../core/boss/chess';
import { sombreroPosition } from '../../core/enemies/sombrero';
import { JEFE_3 } from '../../data/boss-objects';
import { buildCropDuster } from '../models/crop-duster';

/**
 * What the core of JEFE-3 puts on the board and in the sky (GDD §9.2, issue #122): the chess pieces as columns (a rook with
 * battlements, a bishop with a mitre: told apart by form, not only by colour, §13.6), the highlight of the destination
 * cell of an announced move, the check columns that close around their gap and the minibosses called by the phone. A fixed
 * pool is reused every frame and disposed when the scene leaves the arena. The visual box of a piece is its collision box.
 */
export const BOARD_COLORS = {
  rook: 0xe9e2c8,
  rookTop: 0xc9c4b4,
  bishop: 0x4a3a30,
  mitre: 0xd9669a,
  highlight: 0xe0508a,
  check: 0xc0441f,
  balcony: 0x9a6a4a,
  rail: 0x6b4a32,
  sombrero: 0xe6d9b8,
} as const;

/** Pieces on screen at most 3 (JEFE_3.maxPieces) and a margin; check columns and highlights likewise. */
const PIECE_SLOTS = JEFE_3.maxPieces + 2;
const CHECK_SLOTS = 6;
const PIECE_WIDTH = 1.5;
const PIECE_DEPTH = 1;

export interface BoardPool {
  group: Group;
  update(field: AttackField): void;
  dispose(): void;
}

export function buildBoardPool(): BoardPool {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const lambert = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const group = new Group();
  group.name = 'boss:JEFE-3:board';

  // A chess piece: a body that fills its collision box and relief that stays inside it, in two variants (rook, bishop).
  const pieceHeight = JEFE_3.cellSize;
  const pieces = Array.from({ length: PIECE_SLOTS }, (_, slot) => {
    const holder = new Group();
    holder.name = `boss:JEFE-3:piece:${slot}`;
    const make = (color: number): Mesh => {
      const geometry = new BoxGeometry(PIECE_WIDTH, pieceHeight, PIECE_DEPTH);
      geometries.push(geometry);
      return new Mesh(geometry, lambert(color));
    };
    const rook = new Group();
    rook.name = `boss:JEFE-3:piece:${slot}:rook`;
    rook.add(make(BOARD_COLORS.rook));
    for (const x of [-0.5, 0, 0.5]) {
      const geometry = new BoxGeometry(0.35, 0.3, 0.12);
      geometries.push(geometry);
      const battlement = new Mesh(geometry, lambert(BOARD_COLORS.rookTop));
      battlement.position.set(x, pieceHeight / 2 - 0.2, PIECE_DEPTH / 2 + 0.06);
      rook.add(battlement);
    }
    const bishop = new Group();
    bishop.name = `boss:JEFE-3:piece:${slot}:bishop`;
    bishop.add(make(BOARD_COLORS.bishop));
    const mitreGeometry = new ConeGeometry(0.5, 1.1, 4);
    geometries.push(mitreGeometry);
    const mitre = new Mesh(mitreGeometry, lambert(BOARD_COLORS.mitre));
    mitre.position.set(0, 0.5, PIECE_DEPTH / 2 + 0.1);
    bishop.add(mitre);
    holder.add(rook, bishop);
    holder.visible = false;
    group.add(holder);
    return { holder, rook, bishop };
  });

  // The highlight of the destination cell: a translucent pink plane over the cell, never the alert red.
  const highlightGeometry = new BoxGeometry(JEFE_3.cellSize, JEFE_3.cellSize, 0.02);
  geometries.push(highlightGeometry);
  const highlightMaterial = new MeshBasicMaterial({ color: BOARD_COLORS.highlight, transparent: true, opacity: 0.35 });
  materials.push(highlightMaterial);
  const highlights = Array.from({ length: PIECE_SLOTS }, () => {
    const plane = new Mesh(highlightGeometry, highlightMaterial);
    plane.name = 'boss:JEFE-3:board:highlight';
    plane.position.z = PIECE_DEPTH / 2 + 0.25;
    plane.visible = false;
    group.add(plane);
    return plane;
  });

  // The check columns: two boxes each, resized every frame as the gap closes.
  const unit = new BoxGeometry(1, 1, PIECE_DEPTH);
  geometries.push(unit);
  const checkMaterial = lambert(BOARD_COLORS.check);
  const checks = Array.from({ length: CHECK_SLOTS }, (_, slot) =>
    (['upper', 'lower'] as const).map((side) => {
      const box = new Mesh(unit, checkMaterial);
      box.name = `boss:JEFE-3:check:${slot}:${side}`;
      box.visible = false;
      group.add(box);
      return box;
    }),
  );

  // The minibosses called by the phone: the crop duster with its cloud, and the balcony with its sombrero.
  const duster = buildCropDuster();
  duster.group.name = 'boss:JEFE-3:summoned:EN-31';
  group.add(duster.group, ...duster.puffs);
  const balcony = new Group();
  balcony.name = 'boss:JEFE-3:summoned:EN-32';
  const balconyBox = new BoxGeometry(1.4, 0.9, 0.8);
  const railBox = new BoxGeometry(1.5, 0.12, 0.9);
  const hatBrim = new CylinderGeometry(0.4, 0.4, 0.05, 10);
  geometries.push(balconyBox, railBox, hatBrim);
  const balconyBody = new Mesh(balconyBox, lambert(BOARD_COLORS.balcony));
  const rail = new Mesh(railBox, lambert(BOARD_COLORS.rail));
  rail.position.y = 0.5;
  balcony.add(balconyBody, rail);
  balcony.visible = false;
  const sombrero = new Mesh(hatBrim, lambert(BOARD_COLORS.sombrero));
  sombrero.name = 'boss:JEFE-3:summoned:sombrero';
  sombrero.visible = false;
  group.add(balcony, sombrero);

  return {
    group,
    update(field) {
      const shown = field.objects.filter((object) => object.piece);
      pieces.forEach((slot, at) => {
        const object = shown[at];
        slot.holder.visible = object !== undefined;
        highlights[at]!.visible = false;
        if (!object) return;
        slot.holder.position.set(object.x, object.y, 0);
        slot.rook.visible = object.piece!.kind === 'rook';
        slot.bishop.visible = object.piece!.kind === 'bishop';
        const next = object.piece!.next;
        if (next) {
          highlights[at]!.visible = true;
          highlights[at]!.position.x = cellX(next.col);
          highlights[at]!.position.y = cellY(next.row);
        }
      });
      const columns = field.objects.filter((object) => object.check);
      checks.forEach((boxes, at) => {
        const column = columns[at];
        boxes.forEach((box) => (box.visible = column !== undefined));
        if (!column) return;
        const [low, high] = checkBoxes(column, field.phaseTicks);
        for (const [box, source] of [[boxes[1]!, low!], [boxes[0]!, high!]] as const) {
          box.position.set(source.x, source.y, 0);
          box.scale.set(JEFE_3.checkWidth, source.height, 1);
        }
      });
      const called = field.summoned.find((enemy) => enemy.duster);
      duster.group.visible = called !== undefined;
      if (called) duster.group.position.set(called.x, called.y, 0);
      duster.puffs.forEach((puff, at) => {
        const sprayed = called?.duster?.puffs[at];
        puff.visible = sprayed !== undefined;
        if (sprayed) {
          puff.position.set(sprayed.x, sprayed.y, 0);
          puff.scale.setScalar(0.8 + 0.2 * Math.min(1, sprayed.age / 60));
        }
      });
      const thrower = field.summoned.find((enemy) => enemy.thrower);
      balcony.visible = thrower !== undefined;
      if (thrower) balcony.position.set(thrower.x, thrower.y, 0);
      const at = thrower?.thrower ? sombreroPosition(thrower.thrower, thrower) : null;
      sombrero.visible = at !== null;
      if (at) sombrero.position.set(at.x, at.y, 0);
    },
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
      duster.dispose();
    },
  };
}
